import { useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 26 — Diatonic 7th Chord Practice
 * (Inversions + Voice Leading).
 *
 * Walk all seven 7th chords up and down the scale with three patterns:
 * (a) plain block-chord arpeggios, (b) reverse arpeggios, (c) the
 * "City of Stars" arpeggio — the iconic La La Land rhythm pattern that
 * sweeps a 7th chord across two beats with a syncopated lift.
 *
 * Bespoke visual: the City-of-Stars Rhythm Phrase. A two-bar grid with
 * the four-note chord stamped across specific sub-beats. Press play and
 * a ribbon traces through the cells in sequence — you SEE the rhythm
 * before you play it. Three pattern modes (block / reverse / La-La-Land)
 * swap which cells light up.
 */

interface RhythmPattern {
  id: string;
  label: string;
  description: string;
  /** 8 eighth-cells per bar × 2 bars = 16 cells. Each cell is null (rest)
   *  or an index into the chord's notes (0=root, 1=3rd, 2=5th, 3=7th). */
  cells: (number | null)[];
}

const PATTERNS: RhythmPattern[] = [
  {
    id: "block",
    label: "Block arpeggio",
    description: "Up the chord and back down in even eighths, while the left hand holds the root or the whole chord as a block.",
    cells: [0, 1, 2, 3, 2, 1, 0, null,    0, 1, 2, 3, 2, 1, 0, null],
  },
  {
    id: "reverse",
    label: "Reverse zigzag",
    description: "Up the first chord, down the second. Trains opposite direction shifts.",
    cells: [0, 1, 2, 3, null, null, null, null,    3, 2, 1, 0, null, null, null, null],
  },
  {
    id: "lala",
    label: "La La Land",
    description: "Jason's chart: 1-3-5-7, an eighth rest, then 7-5-3 — one bar per chord, with a swing feel.",
    cells: [0, 1, 2, 3, null, 3, 2, 1,    0, 1, 2, 3, null, 3, 2, 1],
  },
];

export default function PianoIntermediate26() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "26");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Diatonic 7th Practice"
          exerciseGoal="Three arpeggio patterns, seven chords each — the third one from City of Stars in La La Land."
          bpm={92}
          difficulty="Advanced"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

// Diatonic 7ths offsets from tonic, root → 7th
const SEVENTHS: { roman: string; offsets: [number, number, number, number] }[] = [
  { roman: "IMaj7",  offsets: [0, 4, 7, 11] },
  { roman: "iim7",   offsets: [2, 5, 9, 12] },
  { roman: "iiim7",  offsets: [4, 7, 11, 14] },
  { roman: "IVMaj7", offsets: [5, 9, 12, 16] },
  { roman: "V7",     offsets: [7, 11, 14, 17] },
  { roman: "vim7",   offsets: [9, 12, 16, 19] },
  { roman: "viim7♭5",offsets: [11, 14, 17, 21] },
];

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [chordIdx, setChordIdx] = useState(0);
  const [patternIdx, setPatternIdx] = useState(0);
  const [activeStep, setActiveStep] = useState(-1);
  const [running, setRunning] = useState(false);
  const handleRef = useRef<RhythmHandle | null>(null);
  const chord = SEVENTHS[chordIdx];
  const pattern = PATTERNS[patternIdx];

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setRunning(false);
    setActiveStep(-1);
  };

  const playPattern = async () => {
    kickAudio();
    stop();
    setRunning(true);
    const events = pattern.cells
      .map((cell, i) =>
        cell == null
          ? null
          : {
              beat: i,
              notes: [midiToPitchName(tonicMidi + chord.offsets[cell], k.useFlats)],
              durationSteps: 1,
            }
      )
      .filter(Boolean) as { beat: number; notes: string[]; durationSteps: number }[];
    handleRef.current = await playRhythm(events, {
      bpm: 92,
      subdivide: 2,            // eighth-note steps (audio/rhythm: 2 = eighth, 4 = sixteenth)
      totalSteps: pattern.cells.length,
      loop: false,
      click: true,             // audible quarter-note click
      beatsPerBar: 4,
      onStep: (s) => setActiveStep(s),
    });
    // Auto-clear after end (totalSteps × eighth-note seconds)
    const totalSec = pattern.cells.length * (60 / 92 / 2);
    window.setTimeout(() => stop(), Math.ceil(totalSec * 1000) + 200);
  };

  const playWalk = async () => {
    kickAudio();
    stop();
    setRunning(true);
    const cellsPerChord = pattern.cells.length;
    const events: { beat: number; notes: string[]; durationSteps: number }[] = [];
    SEVENTHS.forEach((c, ci) => {
      pattern.cells.forEach((cell, i) => {
        if (cell == null) return;
        events.push({
          beat: ci * cellsPerChord + i,
          notes: [midiToPitchName(tonicMidi + c.offsets[cell], k.useFlats)],
          durationSteps: 1,
        });
      });
    });
    const totalSteps = SEVENTHS.length * cellsPerChord;
    handleRef.current = await playRhythm(events, {
      bpm: 92,
      subdivide: 2,
      totalSteps,
      loop: false,
      click: true,
      beatsPerBar: 4,
      onStep: (s) => {
        setActiveStep(s % cellsPerChord);
        setChordIdx(Math.floor(s / cellsPerChord));
      },
    });
    const totalSec = totalSteps * (60 / 92 / 2);
    window.setTimeout(() => stop(), Math.ceil(totalSec * 1000) + 200);
  };

  const masterHighlights: KeyHighlight[] = chord.offsets.map((off, i) => ({
    pc: ((k.tonicPc + off) % 12 + 12) % 12,
    tone: i === 0 ? "root" : "voice",
    label: degreeLabel(off - chord.offsets[0]),
  }));

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The current chord's four voices"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Master keyboard reflects the active chord. Pick a pattern and a
          chord below; the rhythm grid animates the cells your fingers will
          play.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="Where we are" title="From naming chords to playing them rhythmically">
          <p>
            Lesson 24 named the seven diatonic 7ths. This one is about
            getting your fingers <em>fluent</em> on them — by chaining
            them through three rhythm patterns. Each pattern teaches a
            different motor skill:
          </p>
          <ul className="my-3 space-y-1 text-[14px] text-cyan-50/95">
            <li>· <strong>Block arpeggio</strong>: even eighth notes up and back. Steady-hand drill.</li>
            <li>· <strong>Reverse zigzag</strong>: up the first chord, down the next. Direction-flip drill.</li>
            <li>· <strong>La La Land</strong>: 1-3-5-7, rest, 7-5-3 — in C, C-E-G-B, B-G-E. Swing it.</li>
          </ul>
          <PIQuote attribution="Jason · lesson video">
            "One more rhythm I'd like to leave you with to make it quite cool
            would be this inspired from the movie La La Land where in that
            song City of Stars, right?"
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Pick a chord" title="Walk through all seven diatonic 7ths">
          <div className="my-3 grid grid-cols-7 gap-1.5">
            {SEVENTHS.map((c, i) => (
              <motion.button
                key={c.roman}
                layout
                onClick={() => setChordIdx(i)}
                className={`rounded-md px-2 py-1.5 text-center font-mono text-[11px] font-bold ${
                  i === chordIdx
                    ? "bg-amber-400 text-zinc-900"
                    : "bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20"
                }`}
              >
                {c.roman}
              </motion.button>
            ))}
          </div>
          <p className="text-[13px] text-cyan-300/80">
            Active chord: <strong className="text-amber-200">{k.nameForPc(((k.tonicPc + chord.offsets[0]) % 12 + 12) % 12)}{chord.roman.replace(/^[ivIV]+/, "")}</strong>
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Pick a rhythm" title="Three patterns, one chord">
          <div className="my-3 grid grid-cols-1 gap-2 md:grid-cols-3">
            {PATTERNS.map((p, i) => {
              const active = i === patternIdx;
              return (
                <motion.button
                  key={p.id}
                  layout
                  onClick={() => setPatternIdx(i)}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    active
                      ? "border-amber-400/60 bg-amber-500/10"
                      : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                  }`}
                >
                  <div className={`text-[14px] font-bold ${active ? "text-amber-200" : "text-cyan-100"}`}>
                    {p.label}
                  </div>
                  <div className="mt-1 text-[11px] leading-snug text-cyan-300/80">
                    {p.description}
                  </div>
                </motion.button>
              );
            })}
          </div>
          <RhythmPhrase pattern={pattern} chord={chord} activeStep={activeStep} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playPattern}
              disabled={running}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50"
            >
              ▶ Play this chord with this pattern
            </button>
            <button
              onClick={playWalk}
              disabled={running}
              className="rounded-lg border border-amber-400/60 bg-amber-500/10 px-4 py-2 text-sm font-bold text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
            >
              ▶ Walk all 7 chords with this pattern
            </button>
            {running && (
              <button
                onClick={stop}
                className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20"
              >
                ■ Stop
              </button>
            )}
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="The 2-5-1 cadence" title="Now build it musically">
          <p>
            Once each chord is steady, chain three of them in the
            quintessential jazz move:{" "}
            <strong>iim7 → V7 → IMaj7</strong>. In{" "}
            <strong>{k.tonicName}</strong> major that's{" "}
            {k.nameForPc(k.pcFromDegree(2))}m7 →{" "}
            {k.nameForPc(k.pcFromDegree(5))}7 →{" "}
            {k.nameForPc(k.pcFromDegree(1))}Maj7. Use any of the three
            patterns above to articulate it — that's the next lesson, where
            Jason plays the 2-5-1 with the La La Land arpeggio.
          </p>
          <PIInsight>
            <p>
              Voice-leading tip: when you move from one chord to the
              next, find the chord tone that they SHARE and keep it on
              the same finger. The other voices move minimally. This is
              what makes a four-voice arpeggio sound like a hand,
              not a robot.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Practice in three keys" title="Don't fall in love with one">
          <p>
            Jason's standing instruction: every drill should be played in
            at least three keys before it counts as "learned". Use the
            picker in the study deck to switch — the rhythm phrase grid
            stays the same; only the actual notes underneath change.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "A very good ongoing finger drill which even I would do sometimes
            before a gig or before a recording or just like that, the first
            5-10 minutes of a practice session or somewhere in the middle of
            your practice."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The full diatonic 7th walk">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="26"
            label={`Diatonic 7ths walk · ${k.tonicName} major`}
            originalBpm={92}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Block first; reverse second; La La Land third; key change
                last. Don't pile rhythmic complications on top of unfamiliar
                chord shapes.
              </>
            }
            rungs={[
              { title: "Up and down each chord, in order", minutes: 4, accent: "amber", detail: "I → ii → iii → IV → V → vi → vii and back. Arpeggio 1-3-5-7-5-3-1 on each; left hand holds the root or the block. Sing the chord name as you go." },
              { title: "Reverse zigzag — same order",     minutes: 4, accent: "cyan",    detail: "Up the first chord, down the second. Trains your hand to flip directions on chord changes. Slow it down at first." },
              { title: "La La Land pattern",              minutes: 4, accent: "rose",    detail: "1-3-5-7, rest, 7-5-3 on each chord. Swing the eighths and snap on the beat." },
              { title: "Switch to a new key",              minutes: 4, accent: "violet",  detail: "Jason's next key is E♭ major. Run the whole walk again — the fingers change their alignment; the rhythm doesn't." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: RhythmPhrase — 16 eighth-note cells across two bars ── */
function RhythmPhrase({
  pattern,
  chord,
  activeStep,
}: {
  pattern: RhythmPattern;
  chord: { roman: string; offsets: [number, number, number, number] };
  activeStep: number;
}) {
  const VOICE_LABELS = ["root", "3rd", "5th", "7th"];
  const VOICE_COLOURS = ["bg-amber-500", "bg-cyan-500", "bg-emerald-500", "bg-rose-500"];

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ❡ Rhythm phrase · {pattern.label}
        </div>
        <div className="font-mono text-[11px] text-cyan-300/80">
          {chord.roman} · 2 bars · 16 eighths
        </div>
      </div>

      {/* The 16-cell grid */}
      <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(16, minmax(0, 1fr))" }}>
        {pattern.cells.map((cell, i) => {
          const isActive = i === activeStep;
          if (cell == null) {
            return (
              <motion.div
                key={i}
                animate={{ scale: isActive ? 1.08 : 1 }}
                transition={{ duration: 0.1 }}
                className={`flex h-9 items-center justify-center rounded text-[10px] ${
                  isActive ? "bg-cyan-700/60 text-cyan-100" : "bg-zinc-800/50 text-cyan-300/40"
                }`}
              >
                ·
              </motion.div>
            );
          }
          const colour = VOICE_COLOURS[cell];
          const label = VOICE_LABELS[cell];
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, scaleY: 0.5 }}
              whileInView={{ opacity: 1, scaleY: 1 }}
              viewport={{ once: true }}
              animate={{
                scale: isActive ? 1.18 : 1,
                boxShadow: isActive ? "0 0 0 2px #FCD34D, 0 0 12px rgba(252,211,77,0.6)" : "0 0 0 0 transparent",
              }}
              transition={{ duration: 0.1 }}
              className={`flex h-9 flex-col items-center justify-center rounded ${colour} text-[10px] font-bold text-zinc-900`}
            >
              <div>{label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Bar markers */}
      <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] uppercase tracking-wider text-cyan-300/70">
        <div className="text-center">bar 1</div>
        <div className="text-center">bar 2</div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-cyan-300/70">
        {VOICE_LABELS.map((v, i) => (
          <span key={v} className="flex items-center gap-1.5">
            <span className={`inline-block h-3 w-3 rounded ${VOICE_COLOURS[i]}`} />
            {v}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}

/**
 * Name a chord tone from its actual distance above the root, so a m7♭5 shows
 * "♭5" and "♭7" rather than borrowing the labels of a major 7th chord.
 */
function degreeLabel(semitonesAboveRoot: number): string {
  switch (((semitonesAboveRoot % 12) + 12) % 12) {
    case 0:  return "1";
    case 1:  return "♭2";
    case 2:  return "2";
    case 3:  return "♭3";
    case 4:  return "3";
    case 5:  return "4";
    case 6:  return "♭5";
    case 7:  return "5";
    case 8:  return "♭6";
    case 9:  return "6";
    case 10: return "♭7";
    default: return "7";
  }
}
